import NewOfferingButton, {
    DeleteOfferingButton,
    EditOfferingButton,
    frequencyFromExpiresIn,
} from "components/NewOfferingButton";
import OfferingSection from "components/OfferingSection";
import Section from "components/Section";
import ShareButton from "components/ShareButton";
import React, { useState, useEffect } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import ReactTable from "react-table";
import { loadOfferings, setOfferingOrder } from "store/actions/offeringsActions";
import { loadProducts } from "store/actions/productActions";
import { Route, Switch, useHistory, useLocation } from "util/router.js";

const BASE_URL = process.env.REACT_APP_BOOKING_SUCCESS_URL;

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: "none",
    backgroundColor: isDragging ? "var(--light-primary-color)" : null,
    ...draggableStyle,
});

const OfferingTable = ({ data, onReorder }) => {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        setRows(data);
    }, [data]);

    const onDragEnd = result => {
        if (!result.destination) return;
        if (result.destination.index === result.source.index) return;
        const newOrder = reorder(rows, result.source.index, result.destination.index);
        setRows(newOrder);
        onReorder(newOrder);
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                    <table ref={provided.innerRef} className="table">
                        <thead>
                            <tr>
                                <th scope="col">&nbsp;</th>
                                <th scope="col">Name</th>
                                <th scope="col">Direct Purchase Link</th>
                                <th scope="col">Type</th>
                                <th scope="col">Price</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((offering, index) => (
                                <Draggable key={offering.id} draggableId={offering.id} index={index}>
                                    {(provided, snapshot) => (
                                        <tr
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                        >
                                            <td scope="row"><i className="fas fa-grip-vertical"></i></td>
                                            <td>{offering.clickableName}</td>
                                            <td>{offering.buyLink}</td>
                                            <td>{offering.type}</td>
                                            <td>{offering.price}</td>
                                            <td>{offering.actions}</td>
                                        </tr>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </tbody>
                    </table>
                )}
            </Droppable>
        </DragDropContext>
    );
}

function PackagesTable(props) {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const [key, setKey] = React.useState(location.hash ? location.hash.replace("#", "") : "active");

    const { selectedOffering } = props;

    const offerings = useSelector(state =>
        state.instructor.offerings.filter(val => ["pack", "subscription"].includes(val.type))
    );
    const widgetName = useSelector(state => state.instructor.instructor.widgetName);
    const currency = useSelector(
        state => state.instructor.instructor?.instructorProfile?.currency ?? "USD"
    );
    const currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
    });

    const tableData = offerings.map(val => {
        const actions = val.deleted ? (
            <></>
        ) : (
            // we've added some custom button actions
            <div className="d-flex d-row justify-content-end">
                <EditOfferingButton {...props} toEdit={val} />
                <DeleteOfferingButton toDelete={val} />
            </div>
        );

        const link = BASE_URL + "/" + widgetName + "/pricing/checkout/" + val.id;
        const buyLink = (
            <div className="d-flex d-row align-items-center">
                <input
                    style={{ paddingRight: "50px" }}
                    type="text"
                    value={link}
                    readOnly={true}
                    className={"form-control"}
                />
                <ShareButton link={link} style={{ marginLeft: "-50px" }} />
            </div>
        );
        const clickableName = (
            <div className="d-flex d-row align-items-center">
                <a
                    href="#"
                    onClick={e => {
                        e.preventDefault();
                        if (selectedOffering) {
                            selectedOffering(val);
                        }
                        // history.push(`${baseUrl}/class/${key}`)
                    }}
                >
                    {val.name}
                </a>
                {!val.active && (
                    <div
                        style={{
                            backgroundColor: "#fbeeca",
                            color: "black",
                            borderRadius: "4px",
                        }}
                        className="px-2 py-1 ml-2"
                    >
                        Private
                    </div>
                )}
                {val.isOneTimeOffer && (
                    <div
                        style={{ backgroundColor: "#dae8cc", color: "black", borderRadius: "4px" }}
                        className="px-2 py-1 ml-2"
                    >
                        Intro
                    </div>
                )}
            </div>
        );

        var offeringType = "";
        if (val.type === "pack") {
            offeringType = (val.count ?? "0") + " Class Pack";
        } else if (val.type === "subscription") {
            if (val.subscriptionType === "limited") {
                offeringType =
                    frequencyFromExpiresIn(val.expiresIn) +
                    " " +
                    (val.count ?? "0") +
                    " Class Pack";
            } else {
                offeringType = frequencyFromExpiresIn(val.expiresIn) + " Unlimited";
            }
        }

        return {
            ...val,
            price: currencyFormatter.format(val.price),
            offeringType: offeringType,
            actions: actions,
            buyLink: buyLink,
            clickableName: clickableName,
        };
    });

    const activeData = tableData.filter(val => !val.deleted);
    const inactiveData = tableData.filter(val => val.deleted);

    const tableColumns = [
        {
            Header: "Name",
            accessor: "clickableName",
            minWidth: 150,
        },
        {
            Header: "Direct Purchase Link",
            accessor: "buyLink",
            minWidth: 150,
        },
        {
            Header: "Type",
            accessor: "offeringType",
            minWidth: 100,
        },
        {
            Header: "Price",
            accessor: "price",
            width: 100,
        },
        {
            Header: "Actions",
            accessor: "actions",
            sortable: false,
            width: 100,
        },
    ];

    const switchTab = key => {
        history.push(`${location.pathname}#${key}`);
        setKey(key);
    }

    return (
        <div>
            <Tabs activeKey={key} onSelect={switchTab}>
                <Tab eventKey="active" title="Active">
                    <Section>
                        <OfferingTable data={activeData} onReorder={rows => dispatch(setOfferingOrder(rows))} />
                    </Section>
                </Tab>
                <Tab eventKey="inactive" title="Inactive">
                    <Section>
                        {inactiveData.length > 0 ? (
                            <ReactTable
                                data={inactiveData}
                                filterable={false}
                                columns={tableColumns}
                                defaultPageSize={10}
                                showPaginationTop={false}
                                showPaginationBottom={true}
                                className="-highlight"
                            />
                        ) : (
                            <div className="h-100 text-center">
                                Deleted offerings will show here
                            </div>
                        )}
                    </Section>
                </Tab>
            </Tabs>
        </div>
    );
}

function PackagesPage(props) {
    const { match } = props;
    const history = useHistory();
    const dispatch = useDispatch();
    const partnerId = useSelector(state => state.instructor.instructor.platformID);
    useEffect(() => {
        // dispatch
        if (partnerId) {
            dispatch(loadOfferings());
            dispatch(loadProducts());
        }
    }, [dispatch, partnerId]);

    function Packages() {
        return (
            <>
                <Helmet>
                    <title>Packages &amp; Subscriptions</title>
                </Helmet>
                <div className="container px-0">
                    <section>
                        <div className="d-flex d-row justify-content-between align-items-center mb-4">
                            <h2 className="font-weight-bold">Packages &amp; Subscriptions</h2>
                            <NewOfferingButton className="float-right" {...props} />
                        </div>
                        <p>
                            <big>
                                Create additional ways for your students to pay. Packs &amp;
                                subscriptions are a great way to build student loyalty and increase
                                retention.
                            </big>
                        </p>
                        <PackagesTable
                            {...props}
                            history={history}
                            baseUrl={match.url}
                            selectedOffering={offering => {
                                history.push(`${props.match.url}/p/${offering.id}`);
                            }}
                        />
                    </section>
                </div>
            </>
        );
    }

    return (
        <Switch>
            <Route path={`${props.match.url}/p/:offeringId`} component={OfferingSection} />
            <Route path="/">
                <Packages />
            </Route>
        </Switch>
    );
}

export default PackagesPage;
